import { Portal } from "components/portal/Portal";
import { useFormikContext } from "formik";
import { usePrevious } from "hooks/usePrevious";
import React, { FunctionComponent, useEffect, useState } from "react";
import { useIsSinglePerson } from "./hooks/useIsSinglePerson";
import { getTaxCodesForGewest, isPartnerCode } from "./aangiftecodesForm/taxCodesApi";
import { Gewest } from "./personaliaForm/citiesApi";
import { Trans } from "@lingui/macro";

export interface Props {}

const getPartnerCodesFromFilledInValues = (values: Record<string, unknown>) => {
  const { gewest } = values;
  const taxCodes = getTaxCodesForGewest(gewest as Gewest);
  return Object.keys(values)
    .map((prefixedCode) => prefixedCode.substr(1))
    .filter((code) => {
      const taxCode = taxCodes[code];
      if (!taxCode) {
        return false;
      }
      return isPartnerCode({ ...taxCode, code });
    });
};

export const ChangeBurgerlijkeStaatWarning: FunctionComponent<Props> = () => {
  const [{ toBeDeletedPartnerCodes, previousFormValues }, setPartnerCodesForConfirmationDialog] = useState<{
    toBeDeletedPartnerCodes: string[];
    previousFormValues?: any;
  }>({ toBeDeletedPartnerCodes: [] });
  const isSinglePerson = useIsSinglePerson();
  const wasSinglePerson = usePrevious(isSinglePerson);
  const { values, setValues } = useFormikContext<Record<string, unknown>>();
  const previousValues = usePrevious(values);

  useEffect(() => {
    if (isSinglePerson && wasSinglePerson === false) {
      const partnerCodes = getPartnerCodesFromFilledInValues(values);
      setPartnerCodesForConfirmationDialog({
        toBeDeletedPartnerCodes: partnerCodes,
        previousFormValues: previousValues,
      });
    }
  }, [isSinglePerson, values, wasSinglePerson, previousValues]);

  const handleClearPartnerCodes = () => {
    const newValues = { ...values };
    toBeDeletedPartnerCodes.forEach((code) => {
      delete newValues[`c${code}`];
    });
    setValues(newValues);
    setPartnerCodesForConfirmationDialog({ toBeDeletedPartnerCodes: [] });
  };

  const handleCancel = () => {
    setValues(previousFormValues);
    setPartnerCodesForConfirmationDialog({ toBeDeletedPartnerCodes: [] });
  };

  if (toBeDeletedPartnerCodes.length === 0) {
    return null;
  }
  return (
    <Portal>
      <div className="modal fade in" tabIndex={-1} role="dialog" style={{ display: "block" }}>
        <div className="modal-dialog" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">
                <Trans>U verandert van burgerlijke stand. De partnercodes zullen verwijderd worden.</Trans>
              </h4>
            </div>
            <div className="modal-body">
              <p>
                <Trans>Codes met partnergegevens: {toBeDeletedPartnerCodes.join(", ")}</Trans>
              </p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-default" data-dismiss="modal" onClick={handleCancel}>
                <Trans>Annuleren</Trans>
              </button>
              <button type="button" className="btn btn-default" onClick={handleClearPartnerCodes}>
                <Trans>OK</Trans>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
};

ChangeBurgerlijkeStaatWarning.displayName = "ChangeBurgerlijkeStaatWarning";
